import { useEffect, useState } from "react";
import {
  WISHLIST_TYPE_LABEL, WISHLIST_TYPES, WISHLIST_VIEWS,
  type AgentDTO, type WishlistDetailDTO, type WishlistItemDTO, type WishlistStatus, type WishlistType, type WishlistVerdict, type WishlistView,
} from "../../shared/types";
import { useUser } from "../App";
import { Dialog, promptDialog } from "../components/Dialog";
import { MoreOnScroll, usePaged } from "../components/MoreOnScroll";
import { Table } from "../components/Table";
import { Tabs, useTabParam } from "../components/Tabs";
import { TextLink } from "../components/TextLink";
import { Badge, Button, ButtonLink, Choice, ErrorText, Field, inputCls, PageHeader, timeAgo } from "../components/ui";
import { get, post } from "../lib/api";
import { navigate, useLocation } from "../lib/router";

const TYPES = ["all", ...WISHLIST_TYPES] as const;
const VIEW_LABEL: Record<WishlistView, string> = { active: "Open and planned", shipped: "Shipped", declined: "Declined" };
const STATUS_LABEL: Record<WishlistStatus, string> = { open: "Open", planned: "Planned", shipped: "Shipped", declined: "Declined" };
const STATUS_TONE = { open: "zinc", planned: "blue", shipped: "emerald", declined: "red" } as const;
const stillWanted = (status: WishlistStatus) => status === "open" || status === "planned";
const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? "" : "s"}`;
/** Everything below is someone's own words, drawn as text: never Markdown, never markup. */
const words = "min-w-0 break-words whitespace-pre-wrap";

/** "needed by 4 agents across 31 runs", the one line that says agents wanted it; nothing when none has. */
function neededBy(item: WishlistItemDTO): string | null {
  if (!item.runs) return null;
  return `needed by ${plural(Math.max(item.agents, 1), "agent")} across ${plural(item.runs, "run")}`;
}

function askedBy(item: WishlistItemDTO): string {
  if (item.askedByName) return `Asked by ${item.askedByName}`;
  return item.noticed ? "Noticed in runs" : "Asked by someone who left";
}

/** One line per person who voted, with their note. */
function Voters({ voters }: { voters: WishlistDetailDTO["voters"] }) {
  if (!voters.length) return null;
  return (
    <div className="space-y-1.5">
      <div className="text-xs font-medium text-muted">Voted</div>
      {voters.map((v) => (
        <div key={v.id} className="text-sm">
          <span className="font-medium">{v.name}</span>
          {v.note && <span className={`${words} text-muted`}> · {v.note}</span>}
        </div>
      ))}
    </div>
  );
}

/** The runs behind an item, an admin's to read: the agent's own reason, or the command the platform noticed. */
function Evidence({ item }: { item: WishlistDetailDTO }) {
  if (!item.evidence.length) return null;
  return (
    <div className="space-y-1.5">
      <div className="text-xs font-medium text-muted">Runs</div>
      {item.evidence.map((e) => (
        <div key={e.id} className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs">
          <span className="shrink-0 text-faint">{timeAgo(e.at)}</span>
          <span className="shrink-0 font-medium">{e.agentName ?? "unknown agent"}</span>
          {e.source === "reported" ? <Badge tone="emerald">reported</Badge> : <Badge>noticed</Badge>}
          <span className={`${words} flex-1 text-muted`}>{e.reason ?? e.command ?? "—"}</span>
          <TextLink to={`/admin/runs/${e.runId}`} className="shrink-0 text-faint">run</TextLink>
        </div>
      ))}
    </div>
  );
}

/** An admin's verdict: Planned, Shipped, or Declined with a reason. A skill's Planned asks which agent will hold it. */
function Verdict({ item, onDone, onError }: { item: WishlistDetailDTO; onDone: () => void; onError: (message: string) => void }) {
  const [agents, setAgents] = useState<AgentDTO[]>([]);
  const [agentId, setAgentId] = useState("");
  const [busy, setBusy] = useState(false);
  const isSkill = item.type === "skill";
  useEffect(() => {
    if (isSkill) get<AgentDTO[]>("/api/agents").then((all) => setAgents(all.filter((a) => a.youCanUse)), (e) => onError(e.message));
  }, [isSkill]);

  async function give(status: WishlistVerdict) {
    const reason = status === "declined" ? await promptDialog({ title: `Decline "${item.title}"?`, body: "Everyone who asked or voted is told, with your reason.", label: "Why", confirm: "Decline" }) : null;
    if (status === "declined" && !reason?.trim()) return;
    setBusy(true);
    try {
      await post(`/api/wishlist/${item.id}/verdict`, { status, reason, agentId: agentId || undefined });
      onDone();
    } catch (err) {
      onError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-2">
      <div className="text-xs font-medium text-muted">Verdict</div>
      {isSkill && item.status !== "planned" && (
        <Field label="Which agent will hold it?" hint="Planned opens a Draft button: your chat with this agent, with the brief filled in.">
          <select className={inputCls} value={agentId} onChange={(e) => setAgentId(e.target.value)}>
            <option value="">Choose an agent</option>
            {agents.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        </Field>
      )}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" disabled={busy || item.status === "planned" || (isSkill && !agentId)} onClick={() => give("planned")}>Planned</Button>
        <Button size="sm" disabled={busy || item.status === "shipped"} onClick={() => give("shipped")}>Shipped</Button>
        <Button size="sm" variant="danger" disabled={busy || item.status === "declined"} onClick={() => give("declined")}>Declined</Button>
      </div>
    </div>
  );
}

/** One item opened, in the address (`?item=<id>`), which is where a verdict's line links to. */
function ItemDialog({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const me = useUser();
  const [item, setItem] = useState<WishlistDetailDTO | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = () => get<WishlistDetailDTO>(`/api/wishlist/${id}`).then(setItem, (e) => setError(e.message));
  useEffect(() => {
    setItem(null);
    setNote("");
    setError(null);
    void load();
  }, [id]);

  const changed = () => {
    setError(null);
    void load();
    onChanged();
  };

  async function vote() {
    setBusy(true);
    try {
      await post(`/api/wishlist/${id}/vote`, { note });
      setNote("");
      changed();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const canVote = item && stillWanted(item.status) && !item.youVoted;
  return (
    <Dialog
      onClose={onClose}
      size="lg"
      title={item?.title ?? "Loading…"}
      description={item ? `${askedBy(item)} · ${timeAgo(item.createdAt)}` : undefined}
      busy={busy}
      primary={canVote ? <Button variant="primary" disabled={busy} onClick={vote}>Vote</Button> : undefined}
    >
      <ErrorText error={error} />
      {item && (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-2">
            <Badge>{WISHLIST_TYPE_LABEL[item.type]}</Badge>
            <Badge tone={STATUS_TONE[item.status]}>{STATUS_LABEL[item.status]}</Badge>
            <span className="text-xs text-muted">{plural(item.votes, "vote")}</span>
            {neededBy(item) && <span className="text-xs text-muted">{neededBy(item)}</span>}
            {item.agentName && <span className="text-xs text-muted">{item.agentName} will hold it</span>}
          </div>
          {item.brief && <p className={`${words} text-sm`}>{item.brief}</p>}
          {item.reason && <p className={`${words} text-sm text-muted`}>Declined{item.decidedByName ? ` by ${item.decidedByName}` : ""}: {item.reason}</p>}
          {canVote && (
            <Field label="A short note, if you have one" hint="Your name and your note are shown here to everyone.">
              <input className={inputCls} maxLength={280} placeholder="Same, and I also need…" value={note} onChange={(e) => setNote(e.target.value)} />
            </Field>
          )}
          {item.youVoted && <div className="text-sm text-muted">You voted for this.</div>}
          <Voters voters={item.voters} />
          <Evidence item={item} />
          {item.canDraft && <ButtonLink to={`/a/${item.agentSlug}?wishlist=${item.id}`} variant="primary">Draft</ButtonLink>}
          {me.role === "admin" && <Verdict item={item} onDone={changed} onError={setError} />}
        </div>
      )}
    </Dialog>
  );
}

/** A short form that shows what is already on the list as someone types, so one need stays one item. */
function AddDialog({ onClose, onAdded, onOpen }: { onClose: () => void; onAdded: (id: string) => void; onOpen: (id: string) => void }) {
  const [type, setType] = useState<WishlistType>("feature");
  const [title, setTitle] = useState("");
  const [brief, setBrief] = useState("");
  const [matches, setMatches] = useState<WishlistItemDTO[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (title.trim().length < 3) return setMatches([]);
    let current = true;
    const timer = setTimeout(() => {
      get<WishlistItemDTO[]>(`/api/wishlist/matches?${new URLSearchParams({ q: title.trim() })}`).then((rows) => current && setMatches(rows), () => {});
    }, 250);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [title]);

  async function add() {
    setBusy(true);
    try {
      const out = await post<{ item: WishlistItemDTO }>("/api/wishlist", { type, title, brief });
      onAdded(out.item.id);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      onClose={onClose}
      size="lg"
      title="Ask for something"
      description="It goes on the list, with your vote. An admin decides what gets built."
      busy={busy}
      primary={<Button variant="primary" disabled={busy || !title.trim()} onClick={add}>Add</Button>}
    >
      <div className="space-y-4">
        <ErrorText error={error} />
        <Choice label="What it is" value={type} onChange={setType} options={WISHLIST_TYPES.map((t) => ({ value: t, label: WISHLIST_TYPE_LABEL[t] }))} />
        <Field label="What do you need?">
          <input className={inputCls} data-autofocus maxLength={120} value={title} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        {matches.length > 0 && (
          <div className="space-y-1.5">
            <div className="text-xs font-medium text-muted">Already on the list</div>
            {matches.map((m) => (
              <div key={m.id} className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm">
                <span className={`${words} flex-1`}>{m.title}</span>
                <Badge tone={STATUS_TONE[m.status]}>{STATUS_LABEL[m.status]}</Badge>
                <span className="text-xs text-muted">{plural(m.votes, "vote")}</span>
                <TextLink onClick={() => onOpen(m.id)}>{stillWanted(m.status) ? "Vote" : "Open"}</TextLink>
              </div>
            ))}
          </div>
        )}
        <Field label="Why you need it, in a few lines">
          <textarea className={inputCls} rows={3} maxLength={2000} value={brief} onChange={(e) => setBrief(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

/** What has to be built: everyone reads it, adds to it and votes, ranked by people's votes. */
export function WishlistPage() {
  const { pathname, search } = useLocation();
  const [type, setType] = useTabParam(TYPES, "type");
  const [view, setView] = useTabParam(WISHLIST_VIEWS, "view");
  const [adding, setAdding] = useState(false);
  const opened = search.get("item");
  const { rows, done, loading, error, more, reload } = usePaged<WishlistItemDTO>(
    (before) => get(`/api/wishlist?${new URLSearchParams({ view, ...(type === "all" ? {} : { type }), ...(before ? { before } : {}) })}`),
    [type, view],
  );

  /** The item opened over the list, in the address; the filters stay where they were. */
  const open = (id: string | null) => {
    const next = new URLSearchParams(location.search);
    if (id) next.set("item", id);
    else next.delete("item");
    navigate(`${pathname}${next.size ? `?${next}` : ""}`);
  };

  return (
    <div className="scroll-thin h-full overflow-x-hidden overflow-y-auto">
      <PageHeader
        title="Wishlist"
        subtitle="What people and agents need that does not exist yet. Add what is missing, and vote for what you need too."
        actions={<Button variant="primary" onClick={() => setAdding(true)}>Ask for something</Button>}
      />
      <div className="space-y-2 px-4 md:px-6">
        <Tabs label="Type" value={type} onChange={setType} tabs={TYPES.map((t) => ({ key: t, label: t === "all" ? "All" : WISHLIST_TYPE_LABEL[t] }))} />
        <Tabs label="Status" size="sm" value={view} onChange={setView} tabs={WISHLIST_VIEWS.map((v) => ({ key: v, label: VIEW_LABEL[v] }))} />
      </div>
      <div className="p-4 md:p-6">
        <ErrorText error={error} />
        <Table
          rows={rows ?? []}
          rowKey={(item) => item.id}
          onRowClick={(item) => open(item.id)}
          empty={rows ? "Nothing here yet." : undefined}
          columns={[
            {
              header: "What",
              primary: true,
              cell: (item) => (
                <>
                  <div className={`${words} font-medium`}>{item.title}</div>
                  <div className="mt-0.5 text-xs text-muted">{askedBy(item)}</div>
                </>
              ),
            },
            { header: "Type", cell: (item) => <Badge>{WISHLIST_TYPE_LABEL[item.type]}</Badge> },
            { header: "Status", cell: (item) => <Badge tone={STATUS_TONE[item.status]}>{STATUS_LABEL[item.status]}</Badge> },
            { header: "Votes", className: "text-xs text-muted", cell: (item) => plural(item.votes, "vote") },
            { header: "Agents", className: "text-xs text-muted", cell: (item) => neededBy(item) ?? "" },
            {
              header: "",
              align: "right",
              cell: (item) =>
                item.youVoted ? <Badge tone="emerald">You voted</Badge> : stillWanted(item.status) ? (
                  <Button size="sm" onClick={(e) => (e.stopPropagation(), open(item.id))}>Vote</Button>
                ) : null,
            },
          ]}
        />
        <MoreOnScroll onMore={more} done={done} loading={loading} />
      </div>
      {opened && <ItemDialog id={opened} onClose={() => open(null)} onChanged={reload} />}
      {adding && (
        <AddDialog
          onClose={() => setAdding(false)}
          onAdded={(id) => {
            setAdding(false);
            reload();
            open(id);
          }}
          onOpen={(id) => {
            setAdding(false);
            open(id);
          }}
        />
      )}
    </div>
  );
}
